import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSales } from '@/hooks/useSales';
import { useBranch } from '@/context/BranchContext';
import { useAuth } from '@/context/AuthContext';
import { formatMoney, formatDateTime } from '@/lib/format';
import { PAYMENT_LABELS, invoiceStatusMeta } from '@/lib/types';
import DeleteInvoiceDialog from './components/DeleteInvoiceDialog';

const DELETABLE_STATUSES = ['completed', 'partial_refund'];

interface DeleteTarget {
  id: string;
  invoiceNumber: string;
}

export default function SalesPage() {
  const { activeBranchId } = useBranch();
  const { isAdmin } = useAuth();
  const { sales, loading, error, hasMore, loadMore, reload } = useSales(activeBranchId);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const handleDeleteSuccess = () => {
    setDeleteTarget(null);
    reload();
    setToast('Factura eliminada correctamente.');
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Ventas</h1>
          <p className="mt-1 text-foreground-600">Historial de facturas realizadas.</p>
        </div>
        {isAdmin && (
          <Link
            to="/invoices"
            className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3.5 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-file-list-3-line" /> Gestión de facturas
          </Link>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : sales.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-receipt-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">Aún no hay ventas registradas.</p>
          <Link
            to="/pos"
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-shopping-cart-2-line" /> Ir al POS
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-6 rounded-lg border border-background-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-background-100 text-foreground-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Factura</th>
                  {isAdmin && <th className="px-4 py-3 font-medium">Sucursal</th>}
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Cajero</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Método</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Fecha</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {sales.map((sale) => {
                  const meta = invoiceStatusMeta(sale.status);
                  const isVoid =
                    sale.status === 'voided' || sale.status === 'deleted';
                  const canDelete = isAdmin && DELETABLE_STATUSES.includes(sale.status);
                  return (
                    <tr
                      key={sale.id}
                      className={`transition ${isVoid ? 'opacity-55' : 'hover:bg-background-100'}`}
                    >
                      <td className="px-4 py-3">
                        <div
                          className={`font-medium ${
                            isVoid
                              ? 'text-foreground-500 line-through'
                              : 'text-primary-700'
                          }`}
                        >
                          {sale.invoice_number}
                        </div>
                        <span
                          className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-foreground-700">{sale.branch?.name || '—'}</td>
                      )}
                      <td className="px-4 py-3 text-foreground-700">
                        {sale.customer_name || 'Cliente general'}
                      </td>
                      <td className="px-4 py-3 text-foreground-700 hidden md:table-cell">
                        {sale.cashier_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-foreground-700 hidden sm:table-cell">
                        {PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                      </td>
                      <td className="px-4 py-3 text-foreground-600 hidden md:table-cell">
                        {formatDateTime(sale.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-950">
                        {formatMoney(Number(sale.total))}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/sales/${sale.id}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-primary-700 hover:bg-primary-50 cursor-pointer"
                            title="Ver factura"
                          >
                            <i className="ri-eye-line" />
                          </Link>
                          {isAdmin && (
                            <button
                              type="button"
                              disabled={!canDelete}
                              onClick={() =>
                                setDeleteTarget({
                                  id: sale.id,
                                  invoiceNumber: sale.invoice_number,
                                })
                              }
                              title={
                                canDelete
                                  ? 'Eliminar factura'
                                  : 'Esta factura ya no se puede eliminar'
                              }
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-accent-700 hover:bg-accent-50 transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <i className="ri-delete-bin-6-line" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="mt-5 flex justify-center">
              <button
                onClick={loadMore}
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
              >
                <i className="ri-arrow-down-line" /> Cargar más
              </button>
            </div>
          )}
        </>
      )}

      {deleteTarget && (
        <DeleteInvoiceDialog
          saleId={deleteTarget.id}
          invoiceNumber={deleteTarget.invoiceNumber}
          onClose={() => setDeleteTarget(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 rounded-md bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-medium">
          <i className="ri-checkbox-circle-line text-accent-300" /> {toast}
        </div>
      )}
    </div>
  );
}